import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { User, Phone, Mail, MapPin, Plus } from "lucide-react";
import { ClaimDetailed } from "@/api/generated/client/models";
import { EditableField } from "@/components/ui/editable-field";
import { useSidebarValidation } from "@/hooks/use-sidebar-validation";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

interface SidebarInsuredSectionProps {
    claim: ClaimDetailed;
    onUpdate: (field: string, value: any) => Promise<void>;
    canWrite: boolean;
}

export function SidebarInsuredSection({ claim, onUpdate, canWrite }: SidebarInsuredSectionProps) {
    const { validateAndSave } = useSidebarValidation();
    const [showPhone2, setShowPhone2] = useState(!!claim.insuredPhone2);
    const [showEmail2, setShowEmail2] = useState(!!claim.insuredEmail2);

    useEffect(() => {
        setShowPhone2(!!claim.insuredPhone2);
    }, [claim.id, claim.insuredPhone2]);

    useEffect(() => {
        setShowEmail2(!!claim.insuredEmail2);
    }, [claim.id, claim.insuredEmail2]);

    return (
        <Card className="shadow-xs bg-card">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold">Insured Information</h2>
                </div>
            </CardHeader>
            <CardContent className="space-y-2">
                <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <EditableField
                            value={claim.insuredName}
                            onSave={(val) => validateAndSave('insuredName', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm font-medium break-words whitespace-pre-wrap h-auto"
                            placeholder="Insured Name"
                        />
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground mt-2" />
                    <div className="flex-1 min-w-0">
                        <EditableField
                            value={claim.insuredPhone}
                            onSave={(val) => validateAndSave('insuredPhone', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm break-words whitespace-pre-wrap h-auto"
                            placeholder="Add Phone"
                            type="text"
                        />
                        {!showPhone2 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 -mx-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded w-full justify-start mt-0"
                                onClick={() => setShowPhone2(true)}
                                disabled={!canWrite}
                            >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Phone
                            </Button>
                        )}
                    </div>
                </div>
                {showPhone2 && (
                    <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                            <EditableField
                                value={claim.insuredPhone2}
                                onSave={(val) => validateAndSave('insuredPhone2', val, onUpdate)}
                                disabled={!canWrite}
                                className="text-sm break-words whitespace-pre-wrap h-auto"
                                placeholder="Add Phone 2"
                                type="text"
                            />
                        </div>
                    </div>
                )}
                <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground mt-2" />
                    <div className="flex-1 min-w-0">
                        <EditableField
                            value={claim.insuredEmail}
                            onSave={(val) => validateAndSave('insuredEmail', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm break-words whitespace-pre-wrap h-auto"
                            placeholder="Add Email"
                            type="email"
                        />
                        {!showEmail2 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 -mx-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded w-full justify-start mt-0"
                                onClick={() => setShowEmail2(true)}
                                disabled={!canWrite}
                            >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Email
                            </Button>
                        )}
                    </div>
                </div>
                {showEmail2 && (
                    <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                            <EditableField
                                value={claim.insuredEmail2}
                                onSave={(val) => validateAndSave('insuredEmail2', val, onUpdate)}
                                disabled={!canWrite}
                                className="text-sm break-words whitespace-pre-wrap h-auto"
                                placeholder="Add Email 2"
                                type="email"
                            />
                        </div>
                    </div>
                )}
                <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-0.5">Mailing Address</p>
                        <EditableField
                            value={claim.mailingAddress}
                            onSave={(val) => validateAndSave('mailingAddress', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm break-words whitespace-pre-wrap h-auto"
                            placeholder="Same as loss address"
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
